import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

interface SurveyFilterContextType {
  selectedTags: number[];
  searchTerm: string;
  addTag: (tagId: number) => void;
  removeTag: (tagId: number) => void;
  removeAllTags: () => void;
  setSearchTerm: (search: string) => void;
  removeCategory: () => void;
  categoryIdParam: string | null;
}

interface SurveyFilterProviderProps {
  children: ReactNode;
}

const SurveyFilterContext = createContext<SurveyFilterContextType | undefined>(undefined);

export const SurveyFilterProvider: React.FC<SurveyFilterProviderProps> = ({
  children,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryIdParam = useMemo(() => searchParams.get('category_id'), [searchParams]);
  const selectedTagsValueString = useMemo(() => searchParams.get('tag_ids[]')?.split(',').filter(a => !!a).map(Number), [searchParams]);

  const [selectedTags, setSelectedTags] = useState<number[]>(
    Array.isArray(selectedTagsValueString) ? selectedTagsValueString : []
  );
  const [searchTerm, setSearchTermState] = useState<string>('');

  useEffect(() => {
    setSelectedTags(
      Array.isArray(selectedTagsValueString) ? selectedTagsValueString : []
    );
  }, [selectedTagsValueString]);

  const updateTagsWithScroll = async (callback: () => Promise<void> | void) => {
    const scrollPosition = window.scrollY;
    await callback();
    window.scrollTo(0, scrollPosition);
  };

  const addTag = async (tagId: number) => {
    await updateTagsWithScroll(async () => {
      setSelectedTags(currentSelectedTags => {
        const updatedTags = [...new Set([...currentSelectedTags, tagId])];
        setSearchParams({ 'tag_ids[]': updatedTags.join(',') });
        return updatedTags;
      });
    });
  };

  const removeTag = async (tagId: number) => {
    await updateTagsWithScroll(async () => {
      const updatedTags = selectedTags.filter(id => id !== tagId);
      //setSelectedTagsValue(updatedTags.length > 0 ? updatedTags.join(',') : null);

      setSearchParams({ 'tag_ids[]': updatedTags.length > 0 ? updatedTags.join(',') : '' });
      setSelectedTags(updatedTags);
    });
  };

  const removeAllTags = async () => {
    await updateTagsWithScroll(async () => {
      //setSelectedTagsValue(null);
      setSearchParams({}, { replace: true });
      setSelectedTags([]);
    });
  };

  const setSearchTerm = (search: string) => {
    setSearchTermState(search);
  };

  const removeCategory = () => {
    setSearchParams({});
  };

  return (
    <SurveyFilterContext.Provider
      value={{
        selectedTags,
        searchTerm,
        addTag,
        removeTag,
        removeAllTags,
        setSearchTerm,
        removeCategory,
        categoryIdParam,
      }}
    >
      {children}
    </SurveyFilterContext.Provider>
  );
};

export const useSurveyFilterContext = () => {
  const context = useContext(SurveyFilterContext);
  if (!context) {
    return {
      addTag: () => { },
      removeTag: () => { },
      removeAllTags: () => { },
      setSearchTerm: () => { },
      removeCategory: () => { },
      selectedTags: [],
      searchTerm: '',
      categoryIdParam: null,
    };
  }
  return context;
};